import { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";

const who = (r, k) => r[`${k}_name`] || r[`${k}_email`] || `usuario ${(r[`${k}_user`] || "").slice(0, 8)}`;

const Row = ({ r, children, testid }) => (
  <tr className="border-t border-neutral-800" data-testid={testid}>
    <td className="py-2 pr-3 font-mono text-xs"><a href={`/order/${r.order_id}`} target="_blank" rel="noreferrer" className="underline" data-testid={`ratings-order-link-${r.id}`}>{(r.order_id || "").slice(0, 8)}</a>{r.is_auction && <span className="ml-1 text-amber-300">· subasta</span>}</td>
    <td className="py-2 pr-3"><span className="px-2 py-0.5 rounded bg-red-500/15 text-red-300 text-xs">{r.order_status === "SIN_PEDIDO" ? "Pedido inexistente" : r.order_status}</span></td>
    <td className="py-2 pr-3 text-xs">{who(r, "from")} → {who(r, "to")}</td>
    <td className="py-2 pr-3">{r.stars}★</td>
    <td className="py-2 pr-3 text-xs text-neutral-400 max-w-[240px] truncate">{r.comment || "—"}</td>
    <td className="py-2 text-right">{children}</td>
  </tr>
);

const Head = () => (
  <thead><tr className="text-left text-xs text-neutral-500"><th>Pedido</th><th>Estado</th><th>De → a</th><th>Nota</th><th>Comentario</th><th /></tr></thead>
);

export function RatingsIntegrityPanel() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState("");
  const load = () => api.get("/admin/ratings/non-completed").then((r) => setData(r.data)).catch(() => setData({ items: [], removed: [] }));
  useEffect(() => { load(); }, []);
  const remove = async (r) => {
    const reason = window.prompt(`Quitar la calificación ${r.stars}★ de ${who(r, "from")} a ${who(r, "to")} (pedido ${r.order_id.slice(0, 8)}, ${r.order_status}).\nEscribe el motivo:`, "Pedido no completado");
    if (!reason || reason.trim().length < 3) return;
    setBusy(r.id);
    try { await api.post(`/admin/ratings/${r.id}/remove`, { reason: reason.trim() }); toast.success("Calificación quitada"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "No se pudo quitar"); }
    finally { setBusy(""); }
  };
  const restore = async (r) => {
    if (!window.confirm(`¿Restaurar la calificación ${r.stars}★ de ${who(r, "from")} a ${who(r, "to")}?`)) return;
    setBusy(r.id);
    try { await api.post(`/admin/ratings/${r.id}/restore`); toast.success("Calificación restaurada"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "No se pudo restaurar"); }
    finally { setBusy(""); }
  };
  return (
    <div data-testid="ratings-integrity-panel">
      <h2 className="text-xl font-semibold mb-1">Calificaciones de pedidos no completados</h2>
      <p className="text-xs text-neutral-500 mb-4">Solo se califica una venta completada (Liberado, o Resuelta parcial donde el comprador se queda la carta). Al quitar una calificación el promedio se recalcula solo y se revierten sus puntos de rango. Queda en la auditoría y se puede restaurar.</p>
      {!data ? <div className="text-sm text-neutral-500">Cargando…</div> : (
        <>
          <div className="text-sm mb-2">Por revisar: <b data-testid="ratings-integrity-count">{data.items.length}</b></div>
          {data.items.length === 0 ? <div className="text-sm text-neutral-500 mb-6" data-testid="ratings-integrity-empty">No hay calificaciones sobre pedidos no completados.</div> : (
            <table className="w-full text-sm mb-6"><Head /><tbody>
              {data.items.map((r) => (
                <Row key={r.id} r={r} testid={`ratings-integrity-row-${r.id}`}>
                  <Button size="sm" variant="outline" className="border-red-500/50 text-red-300" disabled={busy === r.id} onClick={() => remove(r)} data-testid={`ratings-integrity-remove-${r.id}`}>Quitar</Button>
                </Row>
              ))}
            </tbody></table>
          )}
          <div className="text-sm mb-2">Quitadas: <b data-testid="ratings-removed-count">{data.removed.length}</b></div>
          {data.removed.length > 0 && (
            <table className="w-full text-sm"><Head /><tbody>
              {data.removed.map((r) => (
                <Row key={r.id} r={r} testid={`ratings-removed-row-${r.id}`}>
                  <span className="text-[11px] text-neutral-500 mr-2">{(r.removed_at || "").slice(0, 16).replace("T", " ")} · {r.remove_reason}</span>
                  <Button size="sm" variant="ghost" disabled={busy === r.id} onClick={() => restore(r)} data-testid={`ratings-removed-restore-${r.id}`}>Restaurar</Button>
                </Row>
              ))}
            </tbody></table>
          )}
        </>
      )}
    </div>
  );
}
